const MICROSOFT_OFFICE_SCHEMA_URN = 'urn:schemas-microsoft-com:office:office';

export interface DragEventWithDataTransfer extends DragEvent {
  dataTransfer: DataTransfer;
}

export function isDragEventWithDataTransfer(dragEvent: DragEvent): dragEvent is DragEventWithDataTransfer {
  return !!dragEvent.dataTransfer;
}

export interface ClipboardEventWithData extends ClipboardEvent {
  clipboardData: DataTransfer;
}

export function isClipboardEventWithData(
  clipboardEvent: ClipboardEvent,
): clipboardEvent is ClipboardEventWithData {
  return !!clipboardEvent.clipboardData;
}

/**
 * Determines whether the given data transfer is intended to be a file upload/drop.
 *
 * Microsoft Office apps include an image of the copied content on the clipboard. This function returns
 * `false` for those data transfers.
 */
export function isFileDataTransfer(dataTransfer: DataTransfer): boolean {
  // If we have no files, we can return immediately.
  const { files, types } = dataTransfer;
  if ((!files || files.length === 0) && !types.includes('Files')) {
    return false;
  }

  let hasImageFiles = false;
  if (files) {
    for (let i = 0; i < files.length; i++) {
      hasImageFiles = hasImageFiles || files[i].type.startsWith('image/');
    }
  }

  // If we have no image files, we don't have to worry about Microsoft Office data transfers.
  if (!hasImageFiles) {
    return true;
  }

  // Check for older versions of Microsoft Office.
  if (
    dataTransfer.getData('text/rtf') &&
    (dataTransfer.getData('text/plain') || dataTransfer.getData('text/html'))
  ) {
    // Ignore the image file, use the text/RTF/HTML.
    return false;
  }

  // Check for newer versions of Microsoft Office.
  if (dataTransfer.getData('text/html').includes(MICROSOFT_OFFICE_SCHEMA_URN)) {
    // Ignore the image, use the text/HTML.
    return false;
  }

  // Use the image file!
  return true;
}
